import { useTranslation } from 'react-i18next';
/**
 * TemplateEditor — full-screen dialog for creating/editing contract
 * templates. Includes the paragraph editor, render-options presets,
 * stamp/logo sizing controls, watermark uploader and grouped token
 * catalogue.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import RichTextEditor from '../RichTextEditor';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Plus, Trash2, GripVertical, Lock, EyeOff, AlertTriangle, Check,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from 'lucide-react';
import { API, KIND_LABEL, ProtectedImage } from './_shared';
import { lintTemplate } from './tokenLint';

export default function TemplateEditor({ template, onClose, onSaved }) {
  const { t: tx } = useTranslation();
  const isNew = !template;
  const [t, setT] = useState(template || {
    name: '', kind: 'master', language: 'en',
    party_a_type: 'company', party_b_type: 'employee',
    numbering_pattern: 'C-{{mm}}/{{yy}}/{{seq:04d}}',
    paragraphs: [], header_html: '', footer_html: '',
  });
  const [tokenCatalogue, setTokenCatalogue] = useState({});
  const lint = useMemo(() => lintTemplate(t, tokenCatalogue), [t, tokenCatalogue]);
  const [signatories, setSignatories] = useState([]);
  const [presets, setPresets] = useState([]);
  const refetchPresets = () => {
    axios.get(`${API}/contract-letterhead-presets`).then((r) => {
      setPresets(r.data || []);
    });
  };
  useEffect(() => {
    axios.get(`${API}/contract-template-tokens`).then((r) => {
      setTokenCatalogue(r.data?.catalogue || {});
    });
    axios.get(`${API}/client-stamp-signatories`)
      .then((r) => setSignatories(r.data || []))
      .catch(() => setSignatories([]));
    refetchPresets();
  }, []);

  // Letterhead preset operations. Only captures the brand-band slice of
  // render_options — never logos / paragraphs / sizes.
  const LETTERHEAD_KEYS = [
    'show_top_logo', 'top_logo_align',
    'show_header_band', 'header_left', 'header_center', 'header_right',
    'show_footer_band', 'footer_left', 'footer_center', 'footer_right',
  ];
  const saveLetterheadPreset = async () => {
    const name = window.prompt('Preset name?');
    if (!name) return;
    const opts = t.render_options || {};
    const settings = {};
    LETTERHEAD_KEYS.forEach((k) => { if (opts[k] !== undefined) settings[k] = opts[k]; });
    try {
      await axios.post(`${API}/contract-letterhead-presets`, {
        name: name.trim(),
        description: '',
        settings,
      });
      toast.success(`Preset "${name}" saved`);
      refetchPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };
  const applyLetterheadPreset = (pid) => {
    if (!pid) return;
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    setT({
      ...t,
      render_options: { ...(t.render_options || {}), ...(p.settings || {}) },
    });
    toast.success(`Applied "${p.name}"`);
  };
  const deleteLetterheadPreset = async (pid) => {
    const p = presets.find((x) => x.id === pid);
    if (!p || !window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/contract-letterhead-presets/${pid}`);
      toast.success(tx('auto.ctr.presetDeleted'));
      refetchPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const updateParagraph = (idx, patch) => {
    const next = [...t.paragraphs];
    next[idx] = { ...next[idx], ...patch };
    setT(prev => ({ ...prev, paragraphs: next  }));
  };
  const addParagraph = (afterIdx = null) => {
    const newPara = {
      id: crypto.randomUUID(),
      heading: '', body: '',
      optional: false, locked: false, is_preamble: false,
    };
    const list = t.paragraphs || [];
    if (afterIdx === null || afterIdx >= list.length - 1) {
      setT(prev => ({ ...prev, paragraphs: [...list, newPara]  }));
    } else {
      const next = [...list];
      next.splice(afterIdx + 1, 0, newPara);
      setT(prev => ({ ...prev, paragraphs: next  }));
    }
  };
  const removeParagraph = (idx) => {
    setT(prev => ({ ...prev, paragraphs: t.paragraphs.filter((_, i) => i !== idx)  }));
  };
  const moveParagraph = (idx, dir) => {
    const next = [...t.paragraphs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setT(prev => ({ ...prev, paragraphs: next  }));
  };

  // Numbering aware of preamble: preamble paragraphs are unnumbered;
  // numbered ones increment a counter so display matches the PDF.
  const paragraphNumbers = useMemo(() => {
    const out = [];
    let n = 0;
    for (const p of t.paragraphs || []) {
      if (p.is_preamble) { out.push(null); } else { n += 1; out.push(n); }
    }
    return out;
  }, [t.paragraphs]);

  // Group token keys within a namespace by the first underscore- or
  // dot-separated sub-prefix so they're scannable instead of one
  // 80-token wall. e.g. project.site_address / project.site_city
  // become a "site" sub-group.
  const groupedCatalogue = useMemo(() => {
    const out = {};
    for (const [ns, keys] of Object.entries(tokenCatalogue || {})) {
      const groups = {};
      for (const fullKey of keys) {
        const tail = fullKey.slice(ns.length + 1); // strip "ns."
        let groupKey = 'general';
        if (tail.includes('.')) {
          groupKey = tail.split('.')[0];
        } else if (tail.includes('_')) {
          const prefix = tail.split('_')[0];
          groupKey = prefix;
        }
        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(fullKey);
      }
      const collapsed = { general: [] };
      for (const [g, list] of Object.entries(groups)) {
        if (g === 'general') { collapsed.general.push(...list); continue; }
        if (list.length >= 2) { collapsed[g] = list; }
        else { collapsed.general.push(...list); }
      }
      collapsed.general.sort();
      for (const g of Object.keys(collapsed)) collapsed[g].sort();
      if (collapsed.general.length === 0) delete collapsed.general;
      out[ns] = collapsed;
    }
    return out;
  }, [tokenCatalogue]);

  const save = async () => {
    if (!t.name) { toast.error(tx('auto.ctr.nameRequired')); return; }
    try {
      if (isNew) await axios.post(`${API}/contract-templates`, t);
      else await axios.put(`${API}/contract-templates/${t.id}`, t);
      toast.success(tx('auto.ctr.saved'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New template' : `Edit · ${t.name}`}</DialogTitle>
          <DialogDescription>{tx('auto.ctr.useTokensLike')} <code>&#123;&#123;employee.name&#125;&#125;</code> {tx('auto.ctr.inHeadingsAndBodiesTheyResolve')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>{tx('auto.ctr.name')}</Label>
            <Input value={t.name} onChange={(e) => setT(prev => ({ ...prev, name: e.target.value  }))} data-testid="template-name" />
          </div>
          <div>
            <Label>{tx('auto.ctr.kind')}</Label>
            <Select value={t.kind} onValueChange={(v) => setT(prev => ({ ...prev, kind: v  }))}>
              <SelectTrigger data-testid="template-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{tx('auto.ctr.language')}</Label>
            <Select value={t.language} onValueChange={(v) => setT(prev => ({ ...prev, language: v  }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{tx('auto.ctr.english')}</SelectItem>
                <SelectItem value="pl">{tx('auto.ctr.polski')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{tx('auto.ctr.partyA')}</Label>
            <Select value={t.party_a_type} onValueChange={(v) => setT(prev => ({ ...prev, party_a_type: v  }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">{tx('auto.ctr.companyExpertfix')}</SelectItem>
                <SelectItem value="employee">{tx('auto.ctr.employee')}</SelectItem>
                <SelectItem value="customer">{tx('auto.ctr.customer')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{tx('auto.ctr.partyB')}</Label>
            <Select value={t.party_b_type} onValueChange={(v) => setT(prev => ({ ...prev, party_b_type: v  }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">{tx('auto.ctr.employee')}</SelectItem>
                <SelectItem value="customer">{tx('auto.ctr.customer')}</SelectItem>
                <SelectItem value="company">{tx('auto.ctr.company')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{tx('auto.ctr.numberingPattern')}</Label>
            <Input value={t.numbering_pattern} onChange={(e) => setT(prev => ({ ...prev, numbering_pattern: e.target.value  }))}
              className="font-mono text-xs" data-testid="template-numbering" />
            <div className="text-[10px] text-slate-400 mt-1">
              Tokens: <code>&#123;&#123;mm&#125;&#125;</code> <code>&#123;&#123;yy&#125;&#125;</code> <code>&#123;&#123;yyyy&#125;&#125;</code> <code>&#123;&#123;seq:04d&#125;&#125;</code>
            </div>
          </div>
        </div>

        {/* Default title — kept independent of "Name" so the template name can
            carry a version / dev label while the generated contract gets a
            clean human title. Used when the user doesn't type a title on
            contract creation. */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label>{tx('auto.ctr.defaultContractTitle')}</Label>
            <Input
              value={t.default_contract_title || ''}
              onChange={(e) => setT(prev => ({ ...prev, default_contract_title: e.target.value  }))}
              placeholder="e.g. Contract for Provision of Services"
              data-testid="template-default-title"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Used as the contract&apos;s title when the user doesn&apos;t type one on creation.
              Leave blank to fall back to the template name.
            </div>
          </div>
        </div>

        {/* Default signatories — copied onto each new contract made from
            this template and overridable per contract. Resolve into the
            {{signatory_a.*}} / {{signatory_b.*}} token namespaces. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { field: 'default_signatory_a_id', label: tx('auto.lbl.defaultSignatoryPartyA'), ns: 'signatory_a' },
            { field: 'default_signatory_b_id', label: tx('auto.lbl.defaultSignatoryPartyB'), ns: 'signatory_b' },
          ].map(({ field, label, ns }) => (
            <div key={field}>
              <Label>{label}</Label>
              <Select value={t[field] || 'none'}
                onValueChange={(v) => setT(prev => ({ ...prev, [field]: v === 'none' ? null : v }))}>
                <SelectTrigger data-testid={`template-${ns}`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{tx('auto.ctr.none')}</SelectItem>
                  {signatories.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.role_label ? ` (${s.role_label})` : ''}
                      {!s.customer_id ? ' · Expertfix' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-400 mt-1 font-mono">
                {`{{${ns}.name}}`} · {`{{${ns}.position}}`} · {`{{${ns}.signature}}`}
              </div>
            </div>
          ))}
        </div>

        {/* Token reference — grouped by namespace, click to copy */}
        <Card>
          <CardHeader><CardTitle className="text-xs uppercase text-slate-500">Available tokens ({Object.values(tokenCatalogue).reduce((a, v) => a + v.length, 0)}) — click to copy</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(groupedCatalogue).map(([namespace, subgroups]) => {
              const totalCount = Object.values(subgroups).reduce((a, v) => a + v.length, 0);
              const subKeys = Object.keys(subgroups);
              const hasSubgroups = subKeys.length > 1 || (subKeys.length === 1 && subKeys[0] !== 'general');
              return (
                <details
                  key={namespace}
                  className="rounded border bg-slate-50/50 open:bg-white"
                  open={namespace === 'employee'}
                  data-testid={`token-namespace-${namespace}`}
                >
                  <summary className="cursor-pointer px-2 py-1 text-xs font-medium uppercase text-slate-600 flex items-center gap-2">
                    <span className="font-mono">{namespace}</span>
                    <Badge variant="outline" className="text-[10px]">{totalCount}</Badge>
                  </summary>
                  <div className="px-2 py-2 space-y-1.5">
                    {totalCount === 0 && (
                      <span className="text-[10px] text-slate-400 italic">
                        {namespace === 'input'
                          ? 'Define per contract — any input.your_key works'
                          : 'No sample record in database yet'}
                      </span>
                    )}
                    {hasSubgroups ? (
                      Object.entries(subgroups).map(([group, keys]) => (
                        <details
                          key={group}
                          className="rounded border border-slate-200 bg-white"
                          open={group === 'general'}
                          data-testid={`token-subgroup-${namespace}-${group}`}
                        >
                          <summary className="cursor-pointer px-2 py-1 text-[10px] font-medium text-slate-500 flex items-center gap-2 hover:bg-slate-50">
                            <span className="font-mono">{group}</span>
                            <Badge variant="outline" className="text-[10px]">{keys.length}</Badge>
                          </summary>
                          <div className="px-2 py-1.5 token-cols" data-testid={`token-cols-${namespace}-${group}`}>
                            {keys.map((k) => (
                              <button
                                key={k}
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(`{{${k}}}`);
                                  toast.success(`Copied {{${k}}}`);
                                }}
                                className="inline-block w-full mb-0.5 px-1.5 py-0.5 text-[10px] bg-white hover:bg-blue-50 hover:border-blue-300 border rounded font-mono text-left truncate"
                                title={`{{${k}}}`}
                                data-testid={`token-chip-${k}`}
                              >
                                {`{{${k}}}`}
                              </button>
                            ))}
                          </div>
                        </details>
                      ))
                    ) : (
                      <div className="token-cols px-2 py-1.5">
                        {(subgroups.general || []).map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`{{${k}}}`);
                              toast.success(`Copied {{${k}}}`);
                            }}
                            className="inline-block w-full mb-0.5 px-1.5 py-0.5 text-[10px] bg-white hover:bg-blue-50 hover:border-blue-300 border rounded font-mono text-left truncate"
                            title={`{{${k}}}`}
                            data-testid={`token-chip-${k}`}
                          >
                            {`{{${k}}}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </CardContent>
        </Card>

        {/* Render options — what to include in the PDF.
            Defaults match contract style; turn most off for letters. */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm">{tx('auto.ctr.renderingOptions')}</CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setT({
                    ...t,
                    render_options: {
                      ...(t.render_options || {}),
                      show_title: true,
                      show_contract_number_header: true,
                      show_contract_number_footer: false,
                      show_parties_header: true,
                      show_sign_date_label: true,
                      show_signature_block: true,
                      number_paragraphs: true,
                      page_numbering: true,
                    },
                  })}
                  data-testid="preset-contract"
                  title={tx('auto.ctr.standardContractTitlePartiesBlockNumbering')}
                >
                  {tx('auto.ctr.contractPreset')}
                </Button>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setT({
                    ...t,
                    render_options: {
                      ...(t.render_options || {}),
                      show_title: false,
                      show_contract_number_header: false,
                      show_contract_number_footer: true,
                      show_parties_header: false,
                      show_sign_date_label: false,
                      show_signature_block: false,
                      number_paragraphs: false,
                      page_numbering: true,
                    },
                  })}
                  data-testid="preset-support-letter"
                  title={tx('auto.ctr.supportLetterNoTitleNoParties')}
                >
                  {tx('auto.ctr.supportLetterPreset')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">
              Toggle what appears in the generated PDF. Support letters typically
              switch off the title, parties block, &quot;Signed in&quot; line and signature slots.
            </p>
            {(() => {
              const opts = t.render_options || {};
              const setOpt = (patch) => setT(prev => ({ ...prev, render_options: { ...opts, ...patch }  }));
              const toggle = (key, label, hint) => (
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                    checked={opts[key] !== false}
                    onChange={() => setOpt({ [key]: !(opts[key] !== false) })}
                    className="mt-0.5"
                    data-testid={`render-opt-${key}`} />
                  <span>
                    <span className="font-medium">{label}</span>
                    {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
                  </span>
                </label>
              );
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  {toggle('show_title', 'Show large title at top')}
                  {toggle('show_contract_number_header', 'Show contract number under title')}
                  {toggle('show_contract_number_footer', 'Small reference number at end',
                    'Renders as "Ref: …" — useful when title is hidden')}
                  {toggle('show_parties_header', 'Show two-column Party A / Party B block')}
                  {toggle('show_sign_date_label', 'Show "Signed in X on Y" centered',
                    'Off → date is right-aligned, no label')}
                  {toggle('show_signature_block', 'Show bottom signature slot lines',
                    'Off → if a signatory stamp is attached, it embeds inline at the end')}
                  {toggle('show_signatory_name', 'Show typed signatory name under stamp',
                    'Off when the stamp itself already contains the printed name')}
                  {toggle('number_paragraphs', 'Number paragraphs (§ 1, § 2 …)')}
                  {toggle('page_numbering', 'Show page number footer')}
                </div>
              );
            })()}
            {/* ── Party A / B block — choose which sections render ──
                Only visible when the parties header itself is on. */}
            {(t.render_options?.show_parties_header !== false) && (() => {
              const opts = t.render_options || {};
              const DEFAULT_SECTIONS = [
                'legal_form', 'address', 'country', 'nip_regon',
                'krs', 'pesel', 'email', 'phone', 'bank_account',
                'represented_by',
              ];
              const LABELS = {
                legal_form: 'Legal form (e.g. Sp. z o.o.)',
                address: 'Address (street, postal code, city)',
                country: 'Country',
                nip_regon: 'NIP / REGON',
                krs: 'KRS',
                pesel: 'PESEL',
                email: 'Email',
                phone: 'Phone',
                bank_account: 'Bank account',
                represented_by: 'Represented by',
              };
              // Config is either explicit list (order-preserving) or
              // null → use defaults.
              const current = opts.parties_block_config ?? DEFAULT_SECTIONS;
              const setConfig = (next) => setT({
                ...t,
                render_options: { ...opts, parties_block_config: next },
              });
              const toggleSection = (sid) => {
                const list = current.includes(sid)
                  ? current.filter(x => x !== sid)
                  : [...current, sid];
                setConfig(list);
              };
              const move = (sid, dir) => {
                const i = current.indexOf(sid);
                if (i < 0) return;
                const j = i + dir;
                if (j < 0 || j >= current.length) return;
                const next = [...current];
                [next[i], next[j]] = [next[j], next[i]];
                setConfig(next);
              };
              // Show all sections; enabled ones drag-orderable via up/down.
              const enabled = current;
              const disabled = DEFAULT_SECTIONS.filter(s => !enabled.includes(s));
              return (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    Party block — pick sections &amp; order
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 mb-1">Shown ({enabled.length})</div>
                      <ul className="space-y-1" data-testid="party-block-enabled">
                        {enabled.map((sid, i) => (
                          <li key={sid} className="flex items-center justify-between rounded bg-white border px-2 py-1 text-xs">
                            <span>{LABELS[sid] || sid}</span>
                            <div className="flex items-center gap-0.5">
                              <button type="button" className="text-slate-400 hover:text-slate-700 px-1" title={tx('auto.ctr.moveUp')}
                                onClick={() => move(sid, -1)} disabled={i === 0}
                                data-testid={`party-block-up-${sid}`}>↑</button>
                              <button type="button" className="text-slate-400 hover:text-slate-700 px-1" title={tx('auto.ctr.moveDown')}
                                onClick={() => move(sid, +1)} disabled={i === enabled.length - 1}
                                data-testid={`party-block-down-${sid}`}>↓</button>
                              <button type="button" className="text-red-500 hover:text-red-700 px-1 ml-1" title={tx('auto.ctr.remove')}
                                onClick={() => toggleSection(sid)}
                                data-testid={`party-block-remove-${sid}`}>✕</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 mb-1">Available ({disabled.length})</div>
                      <ul className="space-y-1" data-testid="party-block-available">
                        {disabled.map((sid) => (
                          <li key={sid} className="flex items-center justify-between rounded border bg-white/60 px-2 py-1 text-xs text-slate-600">
                            <span>{LABELS[sid] || sid}</span>
                            <button type="button" className="text-green-600 hover:text-green-700 px-1"
                              onClick={() => toggleSection(sid)}
                              data-testid={`party-block-add-${sid}`}>+ add</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2">
                    The party name is always rendered as the block heading.
                    Rows without data on a given party are automatically skipped.
                  </p>
                </div>
              );
            })()}
            {(() => {
              const opts = t.render_options || {};
              const updateOpt = (patch) => setT({
                ...t,
                render_options: { ...opts, ...patch },
              });
              const SizeAdjuster = ({ title, widthKey, heightKey,
                defWidth, defHeight, maxWidth, maxHeight, testidPrefix }) => {
                const w = Number(opts[widthKey] ?? defWidth);
                const h = Number(opts[heightKey] ?? defHeight);
                const setW = (nw) => updateOpt({
                  [widthKey]: Math.min(maxWidth, Math.max(20, nw)),
                });
                const setH = (nh) => updateOpt({
                  [heightKey]: Math.min(maxHeight, Math.max(20, nh)),
                });
                return (
                  <div className="border-t pt-3 mt-3">
                    <Label>{title} (PDF points, 1 pt ≈ 0.35 mm)</Label>
                    <p className="text-[11px] text-slate-500 mb-2">
                      Max width and height the image is allowed to occupy.
                      Aspect ratio is preserved.
                    </p>
                    <div className="flex flex-wrap gap-4 items-center">
                      {[
                        { label: 'Width', value: w, set: setW, testid: `${testidPrefix}-width` },
                        { label: 'Height', value: h, set: setH, testid: `${testidPrefix}-height` },
                      ].map(({ label, value, set, testid }) => (
                        <div key={label} className="flex items-center gap-2">
                          <Label className="text-xs w-12">{label}</Label>
                          <Button size="icon" variant="outline" className="h-7 w-7"
                            onClick={() => set(value - 10)} data-testid={`${testid}-minus`}>−</Button>
                          <Input className="h-7 w-20 text-center" type="number"
                            value={value}
                            onChange={(e) => set(parseInt(e.target.value || '0', 10) || 0)}
                            data-testid={testid} />
                          <Button size="icon" variant="outline" className="h-7 w-7"
                            onClick={() => set(value + 10)} data-testid={`${testid}-plus`}>+</Button>
                          <span className="text-[11px] text-slate-400">pt</span>
                        </div>
                      ))}
                      <div className="ml-auto text-[11px] text-slate-400 flex items-center gap-2">
                        <span>{tx('auto.ctr.footprintPreview')}</span>
                        <div className="border border-dashed border-slate-300 bg-slate-50"
                          style={{ width: `${w / 2}px`, height: `${h / 2}px` }} />
                      </div>
                    </div>
                  </div>
                );
              };
              return (
                <>
                  <SizeAdjuster
                    title={tx('auto.ctr.stampSignatureSize')}
                    widthKey="stamp_max_width_pt" heightKey="stamp_max_height_pt"
                    defWidth={200} defHeight={120}
                    maxWidth={500} maxHeight={400}
                    testidPrefix="stamp"
                  />
                  <div className="border-t pt-3 mt-3">
                    <Label>{tx('auto.ctr.signatureHeightInTheSignatureBlock')}</Label>
                    <p className="text-[11px] text-slate-500 mb-2">
                      How tall the signature / stamp prints above each signing
                      line. The image is bottom-aligned inside this band, so a
                      bigger value means a bigger signature.
                    </p>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" className="h-7 w-7"
                        onClick={() => setT(prev => ({
                          ...prev,
                          render_options: {
                            ...(prev.render_options || {}),
                            signature_ink_height_pt: Math.max(
                              20, Number((prev.render_options || {}).signature_ink_height_pt ?? 52) - 4),
                          },
                        }))}
                        data-testid="signature-height-minus">−</Button>
                      <Input className="h-7 w-20 text-center" type="number"
                        value={Number(opts.signature_ink_height_pt ?? 52)}
                        onChange={(e) => {
                          const v = Math.min(200, Math.max(20, parseInt(e.target.value || '0', 10) || 20));
                          updateOpt({ signature_ink_height_pt: v });
                        }}
                        data-testid="signature-height" />
                      <Button size="icon" variant="outline" className="h-7 w-7"
                        onClick={() => setT(prev => ({
                          ...prev,
                          render_options: {
                            ...(prev.render_options || {}),
                            signature_ink_height_pt: Math.min(
                              200, Number((prev.render_options || {}).signature_ink_height_pt ?? 52) + 4),
                          },
                        }))}
                        data-testid="signature-height-plus">+</Button>
                      <span className="text-[11px] text-slate-400">{tx('auto.ctr.ptDefault52')}</span>
                    </div>
                  </div>
                  <SizeAdjuster
                    title={tx('auto.ctr.logoSizeCustomerCompany')}
                    widthKey="logo_max_width_pt" heightKey="logo_max_height_pt"
                    defWidth={220} defHeight={80}
                    maxWidth={500} maxHeight={300}
                    testidPrefix="logo"
                  />
                </>
              );
            })()}

            {/* Watermark uploader */}
            {t.id && (
              <div className="border-t pt-3 mt-3">
                <Label>{tx('auto.ctr.backgroundWatermarkOptional')}</Label>
                <p className="text-[11px] text-slate-500 mb-2">
                  PNG, JPEG or SVG embedded as a low-opacity background image on every page.
                  SVG stays sharp at any zoom.
                </p>
                <div className="flex items-start gap-3">
                  {(t.render_options || {}).watermark_image_grid_id ? (
                    <ProtectedImage
                      url={`${API}/contract-templates/${t.id}/watermark`}
                      alt="watermark"
                      className="h-16 max-w-[160px] object-contain border bg-slate-50 p-1"
                      fallbackText="loading watermark…"
                    />
                  ) : (
                    <div className="h-16 w-32 border border-dashed flex items-center justify-center text-[10px] text-slate-400">
                      {tx('auto.ctr.none2')}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,.svg"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('file', file);
                        try {
                          const r = await axios.post(
                            `${API}/contract-templates/${t.id}/watermark`, fd,
                            { headers: { 'Content-Type': 'multipart/form-data' } });
                          setT({
                            ...t,
                            render_options: {
                              ...(t.render_options || {}),
                              watermark_image_grid_id: r.data.watermark_image_grid_id,
                            },
                          });
                          toast.success(tx('auto.ctr.watermarkUploaded'));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Upload failed');
                        }
                      }}
                      data-testid="watermark-upload"
                    />
                    {(t.render_options || {}).watermark_image_grid_id && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500"
                        onClick={async () => {
                          if (!window.confirm(tx('auto.ctr.removeWatermark'))) return;
                          try {
                            await axios.delete(`${API}/contract-templates/${t.id}/watermark`);
                            setT({
                              ...t,
                              render_options: {
                                ...(t.render_options || {}),
                                watermark_image_grid_id: null,
                              },
                            });
                            toast.success(tx('auto.ctr.watermarkRemoved'));
                          } catch { toast.error(tx('auto.ctr.removeFailed')); }
                        }} data-testid="btn-async">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> {tx('auto.ctr.remove')}
                      </Button>
                    )}
                  </div>
                  <div className="ml-auto">
                    <Label className="text-xs">{tx('auto.ctr.opacity')}</Label>
                    <Input type="number" step="0.01" min="0" max="1"
                      className="h-7 w-24"
                      value={(t.render_options || {}).watermark_opacity ?? 0.08}
                      onChange={(e) => setT({
                        ...t,
                        render_options: {
                          ...(t.render_options || {}),
                          watermark_opacity: parseFloat(e.target.value) || 0,
                        },
                      })} />
                  </div>
                </div>
              </div>
            )}

            {/* ─── Top-of-page logo (Item 3) ─────────────────────────── */}
            {(() => {
              const opts = t.render_options || {};
              const setOpt = (patch) => setT({
                ...t, render_options: { ...opts, ...patch },
              });
              return (
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <Label className="text-sm">{tx('auto.ctr.topOfPageCompanyLogo')}</Label>
                      <p className="text-[11px] text-slate-500">
                        Prints the ordering-party logo at the very top of every page.
                        Size respects the &quot;Logo size&quot; controls above.
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!opts.show_top_logo}
                        onChange={(e) => setOpt({ show_top_logo: e.target.checked })}
                        data-testid="render-show-top-logo"
                      />
                      {tx('auto.ctr.show')}
                    </label>
                  </div>
                  {opts.show_top_logo && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="text-slate-500">{tx('auto.ctr.alignment')}</span>
                      {['left', 'center', 'right'].map((a) => (
                        <label key={a} className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name="top-logo-align"
                            value={a}
                            checked={(opts.top_logo_align || 'left') === a}
                            onChange={() => setOpt({ top_logo_align: a })}
                            data-testid={`render-top-logo-align-${a}`}
                          />
                          {a}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ─── Configurable header / footer bands (Item 2) ───────── */}
            {(() => {
              const opts = t.render_options || {};
              const setOpt = (patch) => setT({
                ...t, render_options: { ...opts, ...patch },
              });
              const renderBand = (title, enabledKey, slots) => (
                <div className="border rounded p-2 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold">{title}</Label>
                    <label className="inline-flex items-center gap-1 text-[11px]">
                      <input
                        type="checkbox"
                        checked={!!opts[enabledKey]}
                        onChange={(e) => setOpt({ [enabledKey]: e.target.checked })}
                        data-testid={`render-${enabledKey}`}
                      />
                      {tx('auto.ctr.enable')}
                    </label>
                  </div>
                  {opts[enabledKey] && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {slots.map(([k, label]) => (
                        <div key={k}>
                          <Label className="text-[10px] uppercase text-slate-500">{label}</Label>
                          <Input
                            value={opts[k] || ''}
                            onChange={(e) => setOpt({ [k]: e.target.value })}
                            placeholder={`e.g. {{ordering_party.name}}`}
                            className="h-8 text-xs font-mono"
                            data-testid={`render-${k}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
              return (
                <div className="border-t pt-3 mt-3 space-y-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <Label className="text-sm">Page header &amp; footer</Label>
                      <p className="text-[11px] text-slate-500">
                        Free text plus any <code>{'{{token}}'}</code> from the catalogue.
                        Use <code>{'{{page}}'}</code> and <code>{'{{pages}}'}</code> for live page counters.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {presets.length > 0 && (
                        <Select
                          value=""
                          onValueChange={applyLetterheadPreset}
                        >
                          <SelectTrigger className="h-8 w-44 text-xs" data-testid="letterhead-apply">
                            <SelectValue placeholder={tx('auto.ctr.applyPreset')} />
                          </SelectTrigger>
                          <SelectContent>
                            {presets.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={saveLetterheadPreset}
                        data-testid="letterhead-save"
                      >
                        Save as preset…
                      </Button>
                      {presets.length > 0 && (
                        <details className="relative">
                          <summary className="cursor-pointer text-[11px] text-slate-500 select-none px-1 py-1 hover:text-slate-800">
                            Manage…
                          </summary>
                          <div className="absolute right-0 z-10 mt-1 bg-white border rounded shadow p-2 w-60 max-h-64 overflow-y-auto">
                            {presets.map((p) => (
                              <div key={p.id} className="flex items-center justify-between py-1 text-xs">
                                <span className="truncate" title={p.name}>{p.name}</span>
                                <button
                                  type="button"
                                  onClick={() => deleteLetterheadPreset(p.id)}
                                  className="ml-2 text-red-500 hover:text-red-700 text-[11px]"
                                  data-testid={`letterhead-delete-${p.id}`}
                                >
                                  delete
                                </button>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                  {renderBand('Header band (top of every page)', 'show_header_band', [
                    ['header_left', 'Left'],
                    ['header_center', 'Center'],
                    ['header_right', 'Right'],
                  ])}
                  {renderBand('Footer band (bottom of every page)', 'show_footer_band', [
                    ['footer_left', 'Left'],
                    ['footer_center', 'Center'],
                    ['footer_right', 'Right'],
                  ])}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Paragraphs editor */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Paragraphs ({(t.paragraphs || []).length})</Label>
            <Button size="sm" variant="outline" onClick={() => addParagraph()} data-testid="template-add-paragraph">
              <Plus className="h-3.5 w-3.5 mr-1" /> {tx('auto.ctr.addParagraph')}
            </Button>
          </div>

          {/* Token linter — catches malformed / unknown tokens before
              they reach a generated PDF. */}
          <div
            className={`rounded-lg border p-3 text-sm ${lint.issues.length
              ? 'border-amber-300 bg-amber-50'
              : 'border-emerald-200 bg-emerald-50'}`}
            data-testid="token-lint-panel"
          >
            <div className="flex items-center gap-2 font-medium">
              {lint.issues.length ? (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span data-testid="token-lint-summary">
                    {lint.errorCount} token error{lint.errorCount === 1 ? '' : 's'}
                    {lint.warnCount ? `, ${lint.warnCount} warning${lint.warnCount === 1 ? '' : 's'}` : ''}
                  </span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span data-testid="token-lint-summary">{tx('auto.ctr.allTokensLookGood')}</span>
                </>
              )}
            </div>
            {lint.issues.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="token-lint-issues">
                {lint.issues.slice(0, 12).map((iss, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <Badge variant={iss.level === 'error' ? 'destructive' : 'secondary'} className="shrink-0">
                      {iss.level}
                    </Badge>
                    <span className="font-mono text-slate-700">{iss.token}</span>
                    <span className="text-slate-500">— {iss.message}</span>
                    <span className="ml-auto text-slate-400 whitespace-nowrap">{iss.where}</span>
                  </li>
                ))}
                {lint.issues.length > 12 && (
                  <li className="text-xs text-slate-500">…and {lint.issues.length - 12} more</li>
                )}
              </ul>
            )}
          </div>
          {(t.paragraphs || []).map((p, idx) => (
            <Card
              key={p.id || idx}
              className={`border-l-4 ${p.is_preamble ? 'border-l-amber-400 bg-amber-50/30' : 'border-l-blue-300'}`}
              data-testid={`paragraph-card-${idx}`}
            >
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-start gap-2">
                  <GripVertical className="h-4 w-4 mt-2 text-slate-300 cursor-move" />
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="font-mono text-xs text-slate-400 min-w-[36px]">
                        {p.is_preamble ? '¶' : `§ ${paragraphNumbers[idx]}.`}
                      </span>
                      <Input value={p.heading || ''} onChange={(e) => updateParagraph(idx, { heading: e.target.value })}
                        placeholder={p.is_preamble ? tx('auto.ctr.headingOptionalNoNumber') : tx('auto.ctr.heading')}
                        className="font-semibold flex-1 min-w-[200px]" data-testid={`paragraph-heading-${idx}`} />
                      {(lint.perParagraph[idx] || []).length > 0 && (
                        <Badge
                          variant={lint.perParagraph[idx].some((i) => i.level === 'error') ? 'destructive' : 'secondary'}
                          title={lint.perParagraph[idx].map((i) => `${i.token} — ${i.message}`).join('\n')}
                          data-testid={`paragraph-lint-${idx}`}
                        >
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {lint.perParagraph[idx].length}
                        </Badge>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={tx('auto.ctr.moveUp')} onClick={() => moveParagraph(idx, -1)}>↑</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={tx('auto.ctr.moveDown')} onClick={() => moveParagraph(idx, 1)}>↓</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        title={p.is_preamble ? tx('auto.ctr.unnumberedClickToMakeANumbered') : tx('auto.ctr.makeThisParagraphUnnumberedNoN')}
                        onClick={() => updateParagraph(idx, { is_preamble: !p.is_preamble })}
                        data-testid={`paragraph-preamble-${idx}`}>
                        <span className={`text-xs font-bold ${p.is_preamble ? 'text-amber-600' : 'text-slate-300'}`}>¶</span>
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        title={tx('auto.ctr.optionalCanBeHiddenPerContract')}
                        onClick={() => updateParagraph(idx, { optional: !p.optional })}>
                        <EyeOff className={`h-3.5 w-3.5 ${p.optional ? 'text-amber-500' : 'text-slate-300'}`} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        title={tx('auto.ctr.lockedContractEditorCannotOverride')}
                        onClick={() => updateParagraph(idx, { locked: !p.locked })}>
                        <Lock className={`h-3.5 w-3.5 ${p.locked ? 'text-blue-500' : 'text-slate-300'}`} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        title={tx('auto.ctr.insertANewParagraphBelowThis')}
                        onClick={() => addParagraph(idx)}
                        data-testid={`paragraph-add-below-${idx}`}>
                        <Plus className="h-3.5 w-3.5 text-green-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeParagraph(idx)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                    {/* Body — Tiptap rich text when body_format='html',
                        plain Textarea for legacy paragraphs. Users can
                        switch via the "Rich text" toggle below. */}
                    {(p.body_format || 'text') === 'html' ? (
                      <RichTextEditor
                        value={p.body || ''}
                        onChange={(html) => updateParagraph(idx, { body: html })}
                        placeholder="Body — use {{tokens}} for variable parts"
                        testid={`paragraph-body-rich-${idx}`}
                        align={p.text_align || 'justify'}
                      />
                    ) : (
                      <Textarea value={p.body || ''} onChange={(e) => updateParagraph(idx, { body: e.target.value })}
                        rows={5} placeholder="Body — use {{tokens}} for variable parts"
                        className="font-sans text-sm"
                        style={{ textAlign: p.text_align || 'justify' }}
                        data-testid={`paragraph-body-${idx}`} />
                    )}
                    <label className="flex items-center gap-2 text-[11px] text-slate-500 mt-1 cursor-pointer select-none">
                      <input type="checkbox" checked={(p.body_format || 'text') === 'html'}
                        onChange={(e) => {
                          const next = e.target.checked ? 'html' : 'text';
                          const currentBody = p.body || '';
                          let converted = currentBody;
                          if (next === 'html' && currentBody && !/<[a-z][^>]*>/i.test(currentBody)) {
                            // Plain text → HTML: wrap in <p>{tx('auto.ctr.turnNewlinesInto')} <br>.
                            converted = '<p>' + currentBody.replace(/\n/g, '<br>') + '</p>';
                          } else if (next === 'text' && /<[a-z!/][^>]*>/i.test(currentBody)) {
                            // HTML → plain text: swap common line-break-producing
                            // tags for \n, then strip the rest via DOMParser (which
                            // also decodes entities), otherwise the raw markup
                            // would survive escape() at render time as literal
                            // "<br>" characters in the PDF.
                            const withBreaks = currentBody
                              .replace(/<br\s*\/?>/gi, '\n')
                              .replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, '\n');
                            const temp = document.createElement('div');
                            temp.innerHTML = withBreaks;
                            converted = (temp.textContent || temp.innerText || '')
                              .replace(/\n{3,}/g, '\n\n')
                              .trim();
                          }
                          updateParagraph(idx, { body_format: next, body: converted });
                        }}
                        data-testid={`paragraph-rich-toggle-${idx}`} />
                      {tx('auto.ctr.richTextFormattingListsLinks')}
                    </label>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[10px] text-slate-400 mr-1">{tx('auto.ctr.align')}</span>
                      {[
                        { key: 'left', Icon: AlignLeft, label: tx('auto.lbl.left') },
                        { key: 'center', Icon: AlignCenter, label: tx('auto.lbl.center') },
                        { key: 'right', Icon: AlignRight, label: tx('auto.lbl.right') },
                        { key: 'justify', Icon: AlignJustify, label: tx('auto.lbl.justify') },
                      ].map(({ key, Icon, label }) => {
                        const active = (p.text_align || 'justify') === key;
                        return (
                          <Button
                            key={key}
                            size="icon"
                            variant={active ? 'default' : 'ghost'}
                            className={`h-6 w-6 ${active ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : ''}`}
                            title={label}
                            onClick={() => updateParagraph(idx, { text_align: key })}
                            data-testid={`paragraph-align-${key}-${idx}`}
                          >
                            <Icon className="h-3 w-3" />
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel-btn">{tx('auto.ctr.cancel')}</Button>
          <Button onClick={save} data-testid="template-save">{tx('auto.ctr.saveTemplate')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
